// AI-CONTEXT: See docs/features/dashboard-participation-ladder-growth-card.md
// AI-CONTEXT: See docs/patterns.md#dashboard-static-card
// AI-CONTEXT: See docs/features/participation-ladder-benchmark.md
import { useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { client } from '../../hooks/Client'
import useParticipationLadderFilters from '../../hooks/useParticipationLadderFilters'
import useParticipationBenchmark from '../../hooks/useParticipationBenchmark'
import getBenchmarkStrength, { isBenchmarkStrongEnough } from '../../helpers/dataHelpers/getBenchmarkStrength'
import buildLadderGrowthFlow from '../../helpers/participationHelpers/buildLadderGrowthFlow'
import LadderGrowthSankey from '../Visualisations/participation/LadderGrowthSankey'
import ChartDataTable from '../graphData/ChartDataTable'
import { ladderFlowTable } from '../../helpers/dataHelpers/chartDataTable'
import StaticCardLabel from './StaticCardLabel'
import DashboardCardOptions from './DashboardCardOptions'
import DashboardFilterBar from './DashboardFilterBar'
import DashboardLocationFilter from './DashboardLocationFilter'
import DashboardPersonaRouteFilter from './DashboardPersonaRouteFilter'
import DashboardPersonaSelectFilter from './DashboardPersonaSelectFilter'
import { CATEGORICAL } from '../../helpers/chartPalette'
import usePersonaLocations from '../../hooks/usePersonaLocations'

const formatStepChange = (value) =>
    new Intl.NumberFormat('nl-NL', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
        signDisplay: 'exceptZero',
    }).format(value)

const signClass = (value) => (value > 0 ? 'pos' : value < 0 ? 'neg' : 'neutral')

/**
 * Static dashboard card: average growth in participatieladder treden across the
 * tenant's personas, with a Sankey of begin→current trede movement. Filterable
 * by location, traject and persona. Colour is the only card option.
 *
 * @param {object} props
 * @param {{ Type: string, docid?: string, ColorOverrides?: { primary?: string }, SelectedLocationId?: string, SelectedRouteId?: string, SelectedPersonaId?: string }} props.item - the DashboardItem for this card
 */
const DashboardParticipationLadderGrowthCard = ({ item }) => {
    const history = useHistory()
    const personas = usePersonaLocations()
    const { benchmark, loading: benchmarkLoading } = useParticipationBenchmark()
    const {
        filteredPersonas,
        selectedLocationId,
        setSelectedLocationId,
        selectedRouteId,
        setSelectedRouteId,
        selectedPersonaId,
        setSelectedPersonaId,
        activeFilterCount,
        clear,
    } = useParticipationLadderFilters(personas, { item })

    const flow = useMemo(() => buildLadderGrowthFlow(filteredPersonas), [filteredPersonas])

    // Vaste paletkleur. Deze kaart staat alleen op het dashboard, dus er valt
    // niets te kiezen; de opgeslagen ColorOverrides blijven in Firestore staan
    // maar worden niet meer gelezen. Zie docs/patterns.md#ui-chart-palette.
    const color = CATEGORICAL[0]
    const { averageStepChange, count } = flow

    // Clients only see the benchmark from 'Sterke benchmark' strength up; a
    // weaker benchmark renders as if there is none.
    const benchmarkVisible = isBenchmarkStrongEnough(benchmark)
    const benchmarkGrowth = benchmarkVisible && typeof benchmark?.benchmarkGrowth === 'number' ? benchmark.benchmarkGrowth : null
    const benchmarkStrength = benchmarkGrowth != null
        ? getBenchmarkStrength({
            companyCount: benchmark.companyCount,
            respondentCount: benchmark.respondentCount,
            respondentsPerCompany: benchmark.respondentsPerCompany,
        })
        : null

    const openBenchmarkDetails = () => history.push(`/${client}/participatiebenchmark`)

    return (
        <div className='housevisits-card-body'>
            <StaticCardLabel
                title='Groei participatieladder'
                pill={{ variant: 'personas', label: 'Medewerkers' }}
            />

            <DashboardFilterBar onClear={clear} activeFilterCount={activeFilterCount}>
                <DashboardLocationFilter
                    selectedLocationId={selectedLocationId}
                    onChange={setSelectedLocationId}
                />
                <DashboardPersonaRouteFilter
                    selectedRouteId={selectedRouteId}
                    onChange={setSelectedRouteId}
                />
                <DashboardPersonaSelectFilter
                    personas={Array.isArray(personas) ? personas : []}
                    selectedPersonaId={selectedPersonaId}
                    onChange={setSelectedPersonaId}
                />
            </DashboardFilterBar>

            {count > 0 ? (
                <>
                    <div className='ladder-growth-headline'>
                        <span className={`ladder-growth-headline-value ${signClass(averageStepChange)}`}>
                            {formatStepChange(averageStepChange)}
                        </span>
                        <span className='ladder-growth-headline-unit'>
                            gem. {Math.abs(averageStepChange) === 1 ? 'trede' : 'treden'}
                        </span>
                    </div>
                    <p className='ladder-growth-headline-meta'>
                        over {count} {count === 1 ? 'medewerker' : 'medewerkers'}
                    </p>
                    <div className='housevisits-card-chart'>
                        <LadderGrowthSankey nodes={flow.nodes} links={flow.links} color={color} />
                    </div>
                    {/* Onder het vak met de vaste hoogte, voor de kopie. */}
                    <ChartDataTable table={ladderFlowTable(flow.nodes, flow.links)} />
                </>
            ) : (
                <div className='ladder-growth-empty'>
                    Nog geen gemeten beginsituatie én huidige situatie voor deze selectie.
                </div>
            )}

            {!benchmarkLoading && benchmarkGrowth != null && (
                <div
                    className='ladder-growth-benchmark'
                    role='button'
                    tabIndex={0}
                    onClick={openBenchmarkDetails}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            openBenchmarkDetails()
                        }
                    }}
                >
                    <span className='ladder-growth-benchmark-label'>Benchmark</span>
                    <span className='ladder-growth-benchmark-value'>
                        {formatStepChange(benchmarkGrowth)} {Math.abs(benchmarkGrowth) === 1 ? 'trede' : 'treden'}
                    </span>
                    {benchmarkStrength && (
                        <span className='ladder-growth-benchmark-strength'>{benchmarkStrength.label}</span>
                    )}
                    <span className='ladder-growth-benchmark-more'>Meer info →</span>
                </div>
            )}

            <DashboardCardOptions item={item} />
        </div>
    )
}

export default DashboardParticipationLadderGrowthCard
